import SearchIcon from "@mui/icons-material/Search";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Drawer from "@mui/material/Drawer";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Pagination from "@mui/material/Pagination";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { listSanctions, type TournamentSanction } from "../api";
import {
  SANCTIONS_QUERY_KEY,
  SanctionDetailPanel,
} from "../components/SanctionDetailPanel";
import {
  STATUS_LABEL,
  STATUS_TONE,
  VIEW_FILTERS,
  formatDateRange,
  formatDateTime,
  type QueueFilter,
  type SanctionView,
} from "../sanctionDisplay";

const PAGE_SIZE = 25;
const PANEL_WIDTH = 480;
const VISUALLY_HIDDEN = {
  position: "absolute",
  width: "1px",
  height: "1px",
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
} as const;

function countFor(
  filter: QueueFilter,
  counts: Record<string, number>,
  overdue: number,
): number {
  if (filter.overdue) return overdue;
  return filter.status ? (counts[filter.status] ?? 0) : 0;
}

const STEP_INDEX: Partial<Record<TournamentSanction["status"], number>> = {
  sanctioned: 2,
  results_submitted: 3,
  ratings_applied: 4,
  completed_unrated: 4,
  revoked: 0,
};

/** Four-step bar: requested, sanctioned, results submitted, ratings applied. */
function ProgressBar({ sanction }: { sanction: TournamentSanction }) {
  const done = STEP_INDEX[sanction.status] ?? 1;
  return (
    <Stack
      direction="row"
      spacing={0.5}
      role="img"
      aria-label={`${done} of 4 steps done`}
      sx={{ width: 120 }}
    >
      {[1, 2, 3, 4].map((step) => (
        <Box
          key={step}
          sx={{
            flex: 1,
            height: 4,
            borderRadius: 2,
            bgcolor:
              step <= done
                ? "success.main"
                : step === 3 && sanction.results_overdue
                  ? "error.main"
                  : "action.selected",
          }}
        />
      ))}
    </Stack>
  );
}

function SanctionRow({
  sanction,
  view,
  selected,
  onSelect,
}: {
  sanction: TournamentSanction;
  view: SanctionView;
  selected: boolean;
  onSelect: () => void;
}) {
  const facts = sanction.submitted_facts;
  return (
    <TableRow
      hover
      selected={selected}
      onClick={onSelect}
      sx={{ cursor: "pointer" }}
      aria-selected={selected}
    >
      <TableCell>
        <Typography
          component="button"
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onSelect();
          }}
          sx={{
            all: "unset",
            cursor: "pointer",
            fontSize: 13,
            fontWeight: 600,
            "&:focus-visible": {
              outline: 2,
              outlineColor: "primary.main",
              outlineStyle: "solid",
            },
          }}
        >
          {sanction.tournament_name}
        </Typography>
        <Typography
          color="text.secondary"
          sx={{
            fontSize: 12,
            display: view === "events" ? "block" : { md: "none" },
          }}
        >
          {sanction.org_name}
          {view === "events" && sanction.status === "sanctioned"
            ? ` · results due ${formatDateTime(sanction.results_due_at)}`
            : ""}
        </Typography>
        <Typography
          color="text.secondary"
          sx={{ fontSize: 12, display: { xs: "block", sm: "none" } }}
        >
          {formatDateRange(facts.start_date, facts.end_date)}
        </Typography>
      </TableCell>
      {view === "requests" ? (
        <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
          {sanction.org_name}
        </TableCell>
      ) : null}
      <TableCell
        sx={{ whiteSpace: "nowrap", display: { xs: "none", sm: "table-cell" } }}
      >
        {formatDateRange(facts.start_date, facts.end_date)}
      </TableCell>
      <TableCell
        sx={{ display: { xs: "none", sm: "table-cell" }, whiteSpace: "nowrap" }}
      >
        {view === "events" ? (
          <ProgressBar sanction={sanction} />
        ) : (
          formatDateTime(sanction.submitted_at)
        )}
      </TableCell>
      <TableCell>
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ flexWrap: "wrap", rowGap: 0.5 }}
        >
          <Chip
            size="small"
            variant="outlined"
            color={STATUS_TONE[sanction.status]}
            label={STATUS_LABEL[sanction.status]}
            sx={{ height: 22, fontSize: 12 }}
          />
          {sanction.results_overdue ? (
            <Chip
              size="small"
              color="error"
              label="Overdue"
              sx={{ height: 22, fontSize: 12 }}
            />
          ) : null}
          {sanction.results_late && sanction.status === "results_submitted" ? (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label="Late"
              sx={{ height: 22, fontSize: 12 }}
            />
          ) : null}
        </Stack>
      </TableCell>
    </TableRow>
  );
}

/**
 * Platform Clubs → Sanctioning. "Requests" is the review queue; "Sanctioned
 * events" tracks results and ratings. The selected sanction opens in a side
 * panel (a drawer on narrow screens) and is kept in `?sanction=` so links open it.
 */
export function SanctioningPage({ view }: { view: SanctionView }) {
  const filters = VIEW_FILTERS[view];
  const [params, setParams] = useSearchParams();
  const filterId = filters.some((filter) => filter.id === params.get("filter"))
    ? params.get("filter")!
    : filters[0]!.id;
  const filter = filters.find((candidate) => candidate.id === filterId)!;
  const selectedId = params.get("sanction");
  const [search, setSearch] = useState(params.get("q") ?? "");
  const [query, setQuery] = useState(search.trim());
  const [page, setPage] = useState(1);
  const wide = useMediaQuery("(min-width:1200px)", { noSsr: true });

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);
  useEffect(() => setPage(1), [filterId, query, view]);

  const list = useQuery({
    queryKey: [
      ...SANCTIONS_QUERY_KEY,
      "list",
      filter.status ?? null,
      Boolean(filter.overdue),
      query,
      page,
    ],
    queryFn: () =>
      listSanctions({
        status: filter.status,
        overdue: filter.overdue,
        q: query || undefined,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  const updateParams = (mutate: (next: URLSearchParams) => void) =>
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      mutate(next);
      return next;
    });
  const select = (id: string | null) =>
    updateParams((next) => {
      if (id) next.set("sanction", id);
      else next.delete("sanction");
    });

  const selectFilter = (id: string) =>
    updateParams((next) => {
      next.set("filter", id);
    });
  // With the side panel open on desktop the chips no longer fit one row.
  const compactFilters = wide && Boolean(selectedId);
  const counts = list.data?.counts ?? {};
  const overdueCount = list.data?.overdue_count ?? 0;
  const items = list.data?.items ?? [];
  const total = list.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const panel = selectedId ? (
    <SanctionDetailPanel
      key={selectedId}
      sanctionId={selectedId}
      onClose={() => select(null)}
    />
  ) : null;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns:
          wide && panel ? `minmax(0, 1fr) ${PANEL_WIDTH}px` : "minmax(0, 1fr)",
        gap: 2,
        alignItems: "start",
      }}
    >
      <Typography component="h1" sx={VISUALLY_HIDDEN}>
        {view === "requests" ? "Sanction requests" : "Sanctioned events"}
      </Typography>
      <Box sx={{ minWidth: 0 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ alignItems: { sm: "center" }, mb: 1.25, rowGap: 1 }}
        >
          <TextField
            size="small"
            placeholder={
              view === "requests" ? "Search requests" : "Search events, clubs"
            }
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{
              width: { xs: "100%", sm: 240 },
              "& .MuiInputBase-root": {
                fontSize: 13,
                "@media (pointer: fine)": { minHeight: 32, height: 32 },
              },
            }}
            slotProps={{
              htmlInput: {
                "aria-label": "Search by tournament or club",
                maxLength: 120,
              },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          {compactFilters ? (
            <TextField
              select
              size="small"
              label="Status"
              value={filterId}
              onChange={(event) => selectFilter(event.target.value)}
              sx={{ width: 230, "& .MuiInputBase-root": { fontSize: 13 } }}
            >
              {filters.map((candidate) => (
                <MenuItem
                  key={candidate.id}
                  value={candidate.id}
                  sx={{ fontSize: 13 }}
                >
                  {list.data
                    ? `${candidate.label} (${countFor(candidate, counts, overdueCount)})`
                    : candidate.label}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <Stack
              direction="row"
              spacing={0.75}
              role="group"
              aria-label="Filter by status"
              sx={{ flexWrap: "wrap", rowGap: 0.75 }}
            >
              {filters.map((candidate) => {
                const active = candidate.id === filterId;
                const count = countFor(candidate, counts, overdueCount);
                return (
                  <Chip
                    key={candidate.id}
                    size="small"
                    clickable
                    aria-pressed={active}
                    color={
                      active
                        ? candidate.overdue
                          ? "error"
                          : "success"
                        : "default"
                    }
                    variant="outlined"
                    label={
                      list.data
                        ? `${candidate.label} ${count}`
                        : candidate.label
                    }
                    onClick={() => selectFilter(candidate.id)}
                    sx={{
                      fontSize: 12.5,
                      borderRadius: "6px",
                      fontWeight: active ? 600 : 400,
                      "@media (pointer: fine)": { height: 28, minHeight: 28 },
                    }}
                  />
                );
              })}
            </Stack>
          )}
        </Stack>

        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
          {list.isError ? (
            <Alert
              severity="error"
              sx={{ m: 1.5 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => void list.refetch()}
                  disabled={list.isFetching}
                >
                  Retry
                </Button>
              }
            >
              Sanctions couldn&apos;t be loaded.
            </Alert>
          ) : list.isLoading ? (
            <Box role="status" aria-label="Loading sanctions" sx={{ p: 1.5 }}>
              {[0, 1, 2, 3].map((index) => (
                <Skeleton key={index} height={32} />
              ))}
            </Box>
          ) : items.length === 0 ? (
            <Typography
              role="status"
              color="text.secondary"
              sx={{ p: 3, fontSize: 13, textAlign: "center" }}
            >
              {query
                ? `Nothing matches “${query}”.`
                : `No ${filter.label.toLowerCase()} sanctions.`}
            </Typography>
          ) : (
            <Table
              size="small"
              aria-label={
                view === "requests" ? "Sanction requests" : "Sanctioned events"
              }
              sx={{ "& td, & th": { fontSize: 13 } }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Event</TableCell>
                  {view === "requests" ? (
                    <TableCell
                      sx={{ display: { xs: "none", md: "table-cell" } }}
                    >
                      Club
                    </TableCell>
                  ) : null}
                  <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                    Dates
                  </TableCell>
                  <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                    {view === "events" ? "Progress" : "Submitted"}
                  </TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((sanction) => (
                  <SanctionRow
                    key={sanction.sanction_id}
                    sanction={sanction}
                    view={view}
                    selected={sanction.sanction_id === selectedId}
                    onSelect={() => select(sanction.sanction_id)}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
        {pageCount > 1 ? (
          <Pagination
            size="small"
            count={pageCount}
            page={page}
            onChange={(_, next) => setPage(next)}
            sx={{ mt: 1.5, display: "flex", justifyContent: "flex-end" }}
          />
        ) : null}
      </Box>

      {panel && wide ? (
        <Paper
          variant="outlined"
          sx={{
            position: "sticky",
            top: 16,
            height: "calc(100dvh - 190px)",
            minHeight: 420,
            overflow: "hidden",
          }}
        >
          {panel}
        </Paper>
      ) : null}
      {!wide ? (
        <Drawer
          anchor="right"
          open={Boolean(panel)}
          onClose={() => select(null)}
          slotProps={{
            paper: { sx: { width: { xs: "100%", sm: PANEL_WIDTH } } },
          }}
        >
          {panel}
        </Drawer>
      ) : null}
    </Box>
  );
}

export function SanctionRequestsPage() {
  return <SanctioningPage view="requests" />;
}

export function SanctionedEventsPage() {
  return <SanctioningPage view="events" />;
}
